// Email notifications master toggle component for profile screen
import React, { useState, useEffect } from 'react';
import { useAuthContext } from '../../../hooks/useAuthContext';
import { useTheme } from '../../../context/ThemeContext';
import { 
  getUserNotificationPreferences, 
  updateNotificationPreferences 
} from '../../../services/firebase/user-profile';

const EmailNotificationsToggle = () => {
  const { user } = useAuthContext();
  const { isDarkMode } = useTheme();
  const [enabled, setEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (user?.uid) {
      loadPreferences();
    }
  }, [user?.uid]);

  const loadPreferences = async () => {
    try {
      setLoading(true);
      setError('');

      const result = await getUserNotificationPreferences(user.uid);
      
      if (result.success) {
        setEnabled(result.data.preferences.email?.enabled !== false);
      } else {
        console.error('Failed to load notification preferences:', result.error);
        setError('Failed to load notification settings');
      }
    } catch (error) {
      console.error('Error loading notification preferences:', error);
      setError('Failed to load notification settings');
    } finally {
      setLoading(false);
    }
  };

  const handleToggle = async () => {
    if (updating) return;

    try {
      setUpdating(true);
      setError('');

      const newEnabled = !enabled;
      
      const result = await updateNotificationPreferences(user.uid, {
        email: {
          enabled: newEnabled,
          darkMode: isDarkMode // Use the navigation sidebar theme setting
        }
      });

      if (result.success) {
        setEnabled(newEnabled);
        
        // Show success feedback
        const message = newEnabled 
          ? 'Email notifications enabled' 
          : 'Email notifications disabled';
        
        // You could add a toast notification here
        console.log(message);
      } else {
        console.error('Failed to update notification preferences:', result.error);
        setError('Failed to update notification settings');
      }
    } catch (error) {
      console.error('Error updating notification preferences:', error);
      setError('Failed to update notification settings');
    } finally {
      setUpdating(false);
    }
  };



  if (loading) {
    return (
      <div className={`
        ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'} 
        rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} 
        p-6 sm:p-8 shadow-sm
      `}>
        <div className="animate-pulse">
          <div className={`h-4 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-1/3 mb-2`}></div>
          <div className={`h-3 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-200'} rounded w-2/3`}></div>
        </div>
      </div>
    );
  }

  return (
    <div className="mb-8">
      {/* Section Header */}
      <div className="mb-6">
        <h2 className={`text-xl font-semibold ${isDarkMode ? 'text-gray-100' : 'text-gray-800'} mb-2`}>
          Email Notifications
        </h2>
        <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
          Control your email notification preferences for forum activity
        </p>
      </div>

      {/* Main Container */}
      <div className={`
        ${isDarkMode ? 'bg-gray-800/50' : 'bg-white'} 
        rounded-xl border ${isDarkMode ? 'border-gray-700' : 'border-gray-200'} 
        p-6 sm:p-8 shadow-sm
      `}>
        <div className="space-y-6">
          {/* Master Email Toggle */}
          <div className="flex items-center justify-between">
            <div className="flex-1">
              <h3 className={`text-base font-medium ${isDarkMode ? 'text-gray-100' : 'text-gray-900'} mb-1`}>
                Email Notifications
              </h3>
              <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                {enabled 
                  ? `You will receive email notifications for forum activity${isDarkMode ? ' in dark mode' : ''}`
                  : 'Email notifications are disabled'
                }
              </p>
              {error && (
                <p className="text-sm text-red-600 mt-1">{error}</p>
              )}
            </div>
            
            <div className="ml-6">
              <button
                type="button"
                onClick={handleToggle}
                disabled={updating}
                className={`
                  relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent 
                  transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-indigo-600 focus:ring-offset-2
                  ${enabled ? 'bg-indigo-600' : 'bg-gray-200'}
                  ${updating ? 'opacity-50 cursor-not-allowed' : ''}
                `}
                role="switch"
                aria-checked={enabled}
                aria-labelledby="email-notifications-label"
              >
                <span
                  aria-hidden="true"
                  className={`
                    pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 
                    transition duration-200 ease-in-out
                    ${enabled ? 'translate-x-5' : 'translate-x-0'}
                  `}
                />
              </button>
            </div>
          </div>
        </div>
        
        {!enabled && (
          <div className={`mt-6 p-4 ${isDarkMode ? 'bg-yellow-900/20 border-yellow-700' : 'bg-yellow-50 border-yellow-200'} border rounded-md`}>
            <div className="flex">
              <div className="flex-shrink-0">
                <svg className="h-5 w-5 text-yellow-400" viewBox="0 0 20 20" fill="currentColor">
                  <path fillRule="evenodd" d="M8.257 3.099c.765-1.36 2.722-1.36 3.486 0l5.58 9.92c.75 1.334-.213 2.98-1.742 2.98H4.42c-1.53 0-2.493-1.646-1.743-2.98l5.58-9.92zM11 13a1 1 0 11-2 0 1 1 0 012 0zm-1-8a1 1 0 00-1 1v3a1 1 0 002 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
                </svg>
              </div>
              <div className="ml-3">
                <p className={`text-sm ${isDarkMode ? 'text-yellow-200' : 'text-yellow-800'}`}>
                  You won't receive any email notifications about forum activity.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default EmailNotificationsToggle;